import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { makeBoardPrivate } from '../../records/boardTeamMove';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { setTeamLibraryRefresh } from '../../store/teamsStore';
import { useNotificationStore } from '../../store/notificationStore';
import { requireLocalCapacity } from '../../plans/localGate';
import { isCapped } from '../../plans/capRefusal';
import { countOwnBoardsAndTasks } from '../../extensions/bundled/task-manager/boardsStore';

// Why it failed, in words the user can act on.
export function makePrivateReasonText(err) {
  const status = err?.response?.status;
  const reason = String(err?.message || err || '');
  if (status === 403) return 'Only a team admin or the person who shared this board can make it private.';
  if (status === 404) return 'The board was not found on the server. Wait for sync to finish and try again.';
  if (err?.response?.data?.error === 'scope_limit_reached') return 'Your private boards have reached their record limit.';
  if (reason === 'pending_changes') return 'The latest edits have not synced yet. Check your connection and try again.';
  if (reason === 'offline' || err?.code === 'ERR_NETWORK') return 'You are offline. Making a board private needs a connection.';
  return reason || 'Unknown error';
}

/**
 * Plans v2 card 3.3: does a team board (and its tasks) fit next to the
 * user's own boards? Counts only when the plan caps boards or tasks.
 */
export async function fitsOwnBoards(target) {
  const tasks = Array.isArray(target?.tasks) ? target.tasks.length : Number(target?.taskCount) || 0;
  if (!isCapped('boards') && !isCapped('tasks')) return true;
  const own = await countOwnBoardsAndTasks();
  if (!requireLocalCapacity('boards', own.boards)) return false;
  return tasks === 0 || requireLocalCapacity('tasks', own.tasks, { adding: tasks });
}

/**
 * "Make private" — the team board moves back to the user's private boards
 * (card 9c038d97). The mirror of ShareBoardModal: stays open with a spinner
 * while the server moves it, and says what went wrong when it fails.
 */
export default function MakeBoardPrivateModal({ target, teamName, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const { id, name, teamId } = target;

  const confirm = async () => {
    if (busy) return;
    // Plans v2 card 3.3: the board and its tasks join the user's own, so they
    // count against the plan's caps like new ones. Refused → the upgrade opens.
    if (!(await fitsOwnBoards(target))) return;
    setBusy(true);
    try {
      await makeBoardPrivate(id, teamId);
      notifyMcpSurfaceChanged('boards');
      setTeamLibraryRefresh({ teamId: Number(teamId) || teamId, ts: Date.now() });
      useNotificationStore.getState().success(`“${name}” is now private.`, 'Board made private');
      onDone();
    } catch (err) {
      console.warn('[task-manager] make board private failed:', err);
      useNotificationStore.getState().error(makePrivateReasonText(err), 'Could not make the board private');
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Make board private">
      <div className="flex flex-col" style={{ gap: '0.875rem' }} aria-busy={busy}>
        <p className="task-board-setting-hint">
          “{name}” will leave {teamName || 'the team'} and move to your private boards, with all its
          lists and tasks. Team members will no longer see it. You can share it again later.
        </p>
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {busy ? 'Moving…' : 'Make private'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
